/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    CURRENCY_PHASE2_CENTRALIZED_FORMAT
 * Date:         2026-02-13
 * Modified By:  Claude Code
 *
 * Description:
 * Centralized currency formatting utility. Replaces three scattered helpers
 * (broadcastCardHelpers.formatPrice, broadcastHelpers.formatPrice, formatDualPrice)
 * with a single formatCurrency() that always shows the user's currency.
 *
 * Rule: The user sees everything in their preferred currency. One price only.
 * If convertedPrice exists → show that. Otherwise → show original price.
 *
 * File Size Check:
 * - Current: 65 lines
 * - Replacing with: ~35 lines
 * - Decision: Proceed
 *
 * Related Files:
 * - All inventory/broadcast display components
 */

import { CURRENCY_SYMBOLS } from "../interfaces/Currency";

/**
 * Get currency symbol for a currency code.
 * @param currency - "USD" | "EUR" | "GBP" (defaults to "$")
 */
export function getCurrencySymbol(currency?: string | null): string {
  if (!currency) return "$";
  return CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || "$";
}

/**
 * Format a price for display in the user's preferred currency.
 * Shows ONE price only — the converted price if available, otherwise the original.
 *
 * @param price - Original price value
 * @param currency - Original currency code (used if no conversion)
 * @param convertedPrice - Converted price from backend (optional)
 * @param displayCurrency - User's preferred currency from backend (optional)
 * @returns Formatted string like "$ 1,234" or "€ 5,678", or "" if no price
 */
export function formatCurrency(
  price?: number | null,
  currency?: string | null,
  convertedPrice?: number | null,
  displayCurrency?: string | null
): string {
  // Use converted price if available, otherwise original
  const showPrice = convertedPrice != null ? convertedPrice : price;
  const showCurrency = convertedPrice != null && displayCurrency ? displayCurrency : currency;

  if (showPrice == null) return "";

  const formatted = Math.round(showPrice).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${getCurrencySymbol(showCurrency)} ${formatted}`;
}
